import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import {
  dismissTutorialRequest,
  restoreTutorialRequest,
  handleEmailLoginRequest,
  verifyEmailCodeRequest,
  handleOAuthTokenExchange,
  registerUserRequest,
} from "./useAuthHelpers";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [provisionalUser, setProvisionalUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_URL = process.env.REACT_APP_AUTH_BACKEND_URL;

  const clearError = useCallback(() => setError(null), []);

  useEffect(() => {
    const checkAuth = async () => {
      setAuthLoading(true);
      setError(null);
      try {
        const response = await fetch(`${API_URL}/api/auth/me`, {
            credentials: "include",
        });
        if (response.ok) {
            const data = await response.json();
            setUser(data.user);
        } else if (response.status === 401) {
            setUser(null);
        } else if (response.status >= 500) {
            setError(new Error("Server error while checking authentication."));
        }
      } catch (error) {
        console.error("Failed to fetch user", error);
        setError(error);
      } finally {
        setAuthLoading(false);
      }
    };
    checkAuth();
  }, [API_URL]);

  const startOAuth = (provider) => {
    setError(null);
    // Use crypto.randomUUID() for CSRF state — cryptographically secure entropy.
    // sessionStorage is used (not localStorage) so the token is tab-scoped and
    // automatically cleared when the browser tab closes.
    const state = `${provider}_${crypto.randomUUID()}`;
    sessionStorage.setItem("oauth_state", state);

    let redirectUri = process.env.REACT_APP_OAUTH_REDIRECT_URI;
    if (redirectUri && !redirectUri.startsWith("http")) {
        redirectUri = `https://${redirectUri}`;
    }

    let authUrl;
    if (provider === "google") {
      const params = new URLSearchParams({
        client_id: process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID,
        redirect_uri: redirectUri,
        response_type: "code",
        scope: "openid email profile",
        state: state,
      });
      authUrl = `${process.env.REACT_APP_GOOGLE_OAUTH_AUTHORIZE_URL}?${params}`;
    } else if (provider === "github") {
      const params = new URLSearchParams({
        client_id: process.env.REACT_APP_GITHUB_OAUTH_CLIENT_ID,
        redirect_uri: redirectUri,
        state: state,
        scope: "user:email",
      });
      authUrl = `${process.env.REACT_APP_GITHUB_OAUTH_AUTHORIZE_URL}?${params}`;
    } else if (provider === "microsoft") {
      const params = new URLSearchParams({
        client_id: process.env.REACT_APP_MICROSOFT_OAUTH_CLIENT_ID,
        redirect_uri: redirectUri,
        response_type: "code",
        scope: "openid profile email User.Read offline_access",
        state: state,
      });
      authUrl = `${process.env.REACT_APP_MICROSOFT_OAUTH_AUTHORIZE_URL}?${params}`;
    }

    if (authUrl) {
      window.location.href = authUrl;
    }
  };

  const startEmailLogin = async (email) => {
    setError(null);
    const result = await handleEmailLoginRequest(email, API_URL);
    if (result.error) setError(new Error(result.error));
    return result;
  };

  const verifyEmailCode = async (email, code) => {
    setError(null);
    const result = await verifyEmailCodeRequest(email, code, API_URL);
    if (result.error) {
      setError(new Error(result.error));
    } else if (result.isNew) {
      setProvisionalUser(result.provisionalUser);
    } else if (result.user) {
      setUser(result.user);
    }
    return result;
  };

  const handleOAuthCallback = useCallback(async ({ code, state, redirectUri }) => {
    setError(null);
    const storedState = sessionStorage.getItem("oauth_state");
    // Always clear the state token — whether valid or not — so a replayed or
    // tampered callback cannot be retried with the same token.
    sessionStorage.removeItem("oauth_state");

    if (state !== storedState) {
      const err = new Error("Invalid state parameter");
      setError(err);
      return { error: err.message };
    }

    // Determine provider from state prefix
    let provider = "google"; // fallback default
    if (state && state.includes("_")) {
        const parts = state.split("_");
        // The first part is the provider
        provider = parts[0];
    } else {
        // Fallback to scope heuristic if state format is old or unexpected
        const scope = new URLSearchParams(window.location.search).get("scope") || "";
        const decodedScope = decodeURIComponent(scope);
        if (decodedScope.includes("user:email")) {
            provider = "github";
        } else if (decodedScope.includes("User.Read")) {
            provider = "microsoft";
        }
    }
    
    const result = await handleOAuthTokenExchange(code, provider, redirectUri, API_URL);
    if (result.error) {
      setError(new Error(result.error));
    } else if (result.isNew) {
      setProvisionalUser(result.provisionalUser);
    } else if (result.user) {
      setUser(result.user);
    }
    return result;
  }, [API_URL]);

  const registerUser = useCallback(async (formData) => {
    setError(null);
    if (!provisionalUser) {
      const err = new Error("No provisional user data found for registration.");
      setError(err);
      return { error: err.message };
    }

    const payload = {
      provider: provisionalUser.provider,
      provider_id: provisionalUser.provider_id,
      email: provisionalUser.email,
      name: formData.name || provisionalUser.name,
      profile_data: formData,
    };

    const result = await registerUserRequest(payload, API_URL);
    if (result.error) {
      setError(new Error(result.error));
    } else if (result.success) {
      setUser(result.user);
      setProvisionalUser(null);
    }
    return result;
  }, [provisionalUser, API_URL]);


  const dismissTutorial = useCallback(async () => {
    await dismissTutorialRequest(API_URL);
    setUser((prev) => prev ? { ...prev, show_tutorial: false } : prev);
  }, [API_URL]);

  const restoreTutorial = useCallback(async () => {
    await restoreTutorialRequest(API_URL);
    setUser((prev) => prev ? { ...prev, show_tutorial: true } : prev);
  }, [API_URL]);

  const refreshUser = useCallback(async () => {
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
          credentials: "include",
      });
      if (response.ok) {
          const data = await response.json();
          setUser(data.user);
      } else if (response.status === 401) {
          setUser(null);
      } else if (response.status >= 500) {
          setError(new Error("Server error while refreshing user data."));
      }
    } catch (error) {
      console.error("Failed to refresh user", error);
      setError(error);
    }
  }, [API_URL]);

  const logout = async () => {
    setError(null);
    try {
        const response = await fetch(`${API_URL}/api/auth/logout`, {
            method: "POST",
            credentials: "include",
        });
        if (!response.ok) {
            throw new Error("Logout failed on server");
        }
    } catch (e) {
        console.error("Logout failed", e);
        setError(e);
    }
    // Intentionally clear local session regardless of server response.
    // This prevents the UI getting stuck "logged in" when the server is down.
    // The server cookie has its own expiry; if /api/auth/me succeeds on the
    // next page load (stale cookie), the user will be silently re-authenticated —
    // an acceptable trade-off vs the alternative of an unexitable logged-in state.
    setUser(null);
  };

  const value = {
    user,
    provisionalUser,
    authLoading,
    error,
    clearError,
    startOAuth,
    startEmailLogin,
    verifyEmailCode,
    handleOAuthCallback,
    registerUser,
    dismissTutorial,
    restoreTutorial,
    refreshUser,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  return useContext(AuthContext);
};
